import React, { FunctionComponent, ReactNode, useMemo } from 'react';

import { cn } from '../../lib/css/className/className';
import { TextAlign } from '../TextGeneric/TextGeneric';
import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';
import { StackAlignItems } from '../Stack';

import './MediaItem.css';

import { MediaItemBase, MediaItemBaseProps } from './MediaItemBase/MediaItemBase';

const cls = cn('MediaItem');

type BaseProps = Omit<MediaItemBaseProps, 'titleNode'>;
type MediaItemContentProps =
    | {
          /**
           * Ширина элемента
           */
          width: string | number;
          /**
           * Высота элемента
           */
          height: string | number;
          /**
           * Компонент картинки
           */
          media: undefined;
      }
    | {
          /**
           * Ширина элемента
           */
          width?: string | number;
          /**
           * Высота элемента
           */
          height?: string | number;
          /**
           * Компонент картинки
           */
          media: Exclude<ReactNode, undefined>;
      };

/** Пропсы компонента MediaItem */
export type MediaItemProps = BaseProps &
    MediaItemContentProps & {
        /**
         * Основной текст (заголовок или абзац)
         */
        title?: ReactNode;
        /**
         * Дополнительный поясняющий текст, показывается только при наличии title
         */
        caption?: string;
    };

function getTextAlign(alignItems: StackAlignItems | undefined): TextAlign | undefined {
    switch (alignItems) {
        case 'center':
            return 'center';
        case 'flex-end':
            return 'right';
        case 'flex-start':
            return 'left';
        default:
            return undefined;
    }
}

/**
 * Компонент обертка поверх картинки для отображения дополнительных контролов на картинке и текста под картинкой
 * Для позиционирования контролов необходимо использовать вспомогательный компонент `<MediaControl />`
 *
 * `<MediaControl />` в первую очередь предназначен для отображения следующих компонентов
 *
 * `<FloatingButton />`, `<Label />`, `<ClickableIcon />`
 *
 * Пример использования:
 *
 * ```tsx
 *   <MediaItem
 *      title="Заголовок"
 *      caption="Описание"
 *      controls={(
 *          <>
 *              <MediaControl isAlwaysVisible position="bottom-left">
 *                  <Label color="black-negative" size="4xs">
 *                      Видеодополнение
 *                  </Label>
 *              </MediaControl>
 *              <MediaControl isAlwaysVisible position="center">
 *                  <FloatingButton size='l' color='contrast' icon={IconPlayFilled}/>
 *              </MediaControl>
 *              <MediaControl position="top-left">
 *                  <ClickableIcon>
 *                      <IconSelectYellowFilled size="24" />
 *                  </ClickableIcon>
 *              </MediaControl>
 *          </>
 *      )}
 *      media={<Image/>}
 *  />
 *  ```
 */
export const MediaItem: FunctionComponent<MediaItemProps> = function MediaItem(props) {
    const { title, caption, className, 'data-testid': dataTestId = 'MediaItem', ...baseProps } = props;

    const { alignItems } = baseProps;

    const titleNode = useMemo<ReactNode>(() => {
        if (title || caption) {
            return (
                <TextGenericWithSkin
                    data-testid={`${dataTestId}.Title`}
                    className={cls('title')}
                    skinSize="xs"
                    textSize="current-main + current-body"
                    textAlign={getTextAlign(alignItems)}
                    caption={caption}
                >
                    {title}
                </TextGenericWithSkin>
            );
        }

        return null;
    }, [alignItems, caption, dataTestId, title]);

    return (
        <MediaItemBase
            data-testid={dataTestId}
            className={cls(null, [className])}
            titleNode={titleNode}
            {...baseProps}
        />
    );
};
